import { type LayerKey, OperationLayerMap } from '@festina-life/api'
import { useMemo } from 'react'

import { useMetaModelContext } from '../context/metaModelContext'
import { collectAllNodesFromTree } from '../uiModels/api/metaModel/helpers'
import {
  filterShadowedOperationsForCondition,
  getAllShadowedTemplateCreateOperations,
} from '../uiModels/templateOperations/helpers'
import type {
  IndexedTemplateOperations,
  IndexedTemplateOperationWithMetaData,
  ShadowedOperationsPerTemplateIdAndDate,
} from '../uiModels/templateOperations/types'
import type { ConfigurationItemsByBlock } from './operationsContext'

export const useShadowedTemplateOperations = ({
  templateOperations,
  layers,
  operationCondition,
}: {
  templateOperations: ConfigurationItemsByBlock<IndexedTemplateOperations>
  layers: LayerKey[]
  operationCondition?: (
    operation: IndexedTemplateOperationWithMetaData,
  ) => boolean
}): Map<string, OperationLayerMap<ShadowedOperationsPerTemplateIdAndDate>> => {
  const { blockTopology } = useMetaModelContext()
  const allBlocks = useMemo(
    () => (blockTopology ? collectAllNodesFromTree(blockTopology) : []),
    [blockTopology],
  )

  return useMemo(
    () =>
      new Map(
        allBlocks
          .map(
            (block) =>
              [
                block,
                new OperationLayerMap<ShadowedOperationsPerTemplateIdAndDate>(
                  layers
                    .map((layer) => {
                      const templateOperationsForLayer =
                        templateOperations.get(block)?.get(layer) ??
                        new Map<string, IndexedTemplateOperations>()

                      const allShadowed =
                        getAllShadowedTemplateCreateOperations(
                          templateOperationsForLayer,
                        )

                      if (!operationCondition) {
                        return [layer, allShadowed] as const
                      }

                      return [
                        layer,
                        filterShadowedOperationsForCondition(
                          new OperationLayerMap([[layer, allShadowed]]),
                          operationCondition,
                        ).get(layer) ?? new Map(),
                      ] as const
                    })
                    .filter(
                      ([_, operationsForLayer]) => operationsForLayer.size > 0,
                    ),
                ),
              ] as const,
          )
          .filter(([_, operationLayerMap]) => operationLayerMap.size > 0),
      ),
    [layers, templateOperations, allBlocks, operationCondition],
  )
}
